import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Manifesto from '@/components/Manifesto';
import { api, buildWhatsAppLink, buildWholesaleWhatsAppLink, BRAND_ADDRESS } from '@/lib/api';
import { ArrowUpRight, Truck } from 'lucide-react';

const STATS = [
  { n: '25+', label: 'Years in Heritage' },
  { n: '13', label: 'Cultural Categories' },
  { n: '1000+', label: 'Handcrafted Pieces' },
  { n: '1999', label: 'Est.' },
];

export default function About() {
  const [manifestoImages, setManifestoImages] = useState([]);
  useEffect(() => {
    api.get('/home').then((r) => setManifestoImages(r.data?.manifesto_images || [])).catch(() => {});
  }, []);
  return (
    <div className="pt-32 pb-24" data-testid="about-page">
      <div className="px-6 sm:px-10 max-w-[1600px] mx-auto">
        <p className="eyebrow mb-6">About Us · Since 1999</p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif-display text-6xl sm:text-8xl lg:text-9xl tracking-tighter leading-[0.85] max-w-5xl"
        >
          A house built on<br/><em className="text-[color:var(--mb-primary)] not-italic">Nepali heritage.</em>
        </motion.h1>

        {/* Long-form */}
        <div className="mt-20 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7 space-y-8 text-lg leading-relaxed text-[color:var(--mb-text)] font-light">
            <p>
              Mahabir Bastralaya was founded in <strong className="font-medium text-[color:var(--mb-primary)]">1999</strong> in Jaigaon — a bustling border town where Bengal, Bhutan and Nepal meet. What began as a modest storefront on NS Road has, across more than two decades, grown into a dedicated house of <strong className="font-medium">Nepali cultural clothing</strong>.
            </p>
            <p>
              We specialise in the traditional dresses of the Himalayan communities we call our neighbours and family — the <strong className="font-medium">Tibetan Bakhu</strong>, <strong className="font-medium">Tamang Dress</strong>, <strong className="font-medium">Rai Dress</strong>, <strong className="font-medium">Gurung Dress</strong>, <strong className="font-medium">Mangar Dress</strong>, <strong className="font-medium">Subba Dress</strong>, <strong className="font-medium">Limbu Dress</strong>, <strong className="font-medium">Newari &amp; Pradhan Dress</strong>, <strong className="font-medium">Lepcha Dress</strong>, <strong className="font-medium">Dhaka Saree</strong>, <strong className="font-medium">Choubandi Choli</strong>, and <strong className="font-medium">Bhutanese Dress</strong>. Each carries the story of its people; we simply try to be worthy stewards.
            </p>
            <p>
              Alongside our ready-to-wear collection, our proudest speciality remains <strong className="font-medium">Made to Order</strong>. Bridal Bakhu, ceremonial Tamang sets, community-specific silhouettes, custom sizing and fabric — our master tailors work with you from the first measurement to the final hem. For families across Sikkim, Darjeeling, Kalimpong, Bhutan and the North East, our tailoring room has quietly outfitted milestones — weddings, cultural festivals, first-day-at-college, retirement portraits — for a full generation. Whatever you choose, ready-made or made-to-order, <strong className="font-medium">we ship across India</strong>.
            </p>
            <p>
              We also cater to <strong className="font-medium">wholesale enquiries</strong> for our traditional Nepali attires — for boutiques, cultural organisations and community events. Reach out on WhatsApp for wholesale pricing and availability.
            </p>
            <p>
              We are proud to be a small, family-run house. Nothing here is mass produced. Every drape, embroidery and stitch is handled with the same care as if it were meant for one of our own.
            </p>
          </div>

          {/* Stats */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-6 self-start lg:sticky lg:top-32">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="border border-[color:var(--mb-border)] p-6 sm:p-8 bg-[color:var(--mb-surface)]"
              >
                <p className="font-serif-display text-5xl sm:text-6xl text-[color:var(--mb-primary)] leading-none">{s.n}</p>
                <p className="mt-3 text-[0.65rem] tracking-[0.3em] uppercase text-[color:var(--mb-text-muted)]">{s.label}</p>
              </motion.div>
            ))}
            <div className="col-span-2 border border-[color:var(--mb-border)] p-6 sm:p-8 bg-[color:var(--mb-primary)] text-white">
              <p className="font-serif-display text-2xl sm:text-3xl leading-tight italic">
                "We don't just sell dresses. We hand you a piece of your people's story to wear."
              </p>
              <p className="mt-4 text-[0.65rem] tracking-[0.3em] uppercase text-white/70">— The Mahabir Family</p>
            </div>
          </div>
        </div>

        {/* Made to Order highlight */}
        <div className="mt-24 border-t border-[color:var(--mb-border)] pt-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-4">Our Speciality</p>
            <h2 className="font-serif-display text-5xl sm:text-6xl leading-[0.95] tracking-tight">
              Made to Order,<br/><em className="text-[color:var(--mb-primary)] not-italic">crafted to your measure.</em>
            </h2>
            <p className="mt-6 max-w-xl text-lg text-[color:var(--mb-text-muted)] font-light leading-relaxed">
              Choose your community's silhouette, your fabric and your measurements — we will bring the piece to life. Perfect for weddings, cultural celebrations, family portraits and heirloom pieces you'll pass down. Typical lead time: 2 to 6 weeks.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href={buildWhatsAppLink('a Made to Order enquiry')} target="_blank" rel="noreferrer" data-testid="about-mto-cta" className="btn-red">
                Start a Custom Order <ArrowUpRight size={14} />
              </a>
              <Link to="/products?category=made-to-order" className="btn-outline">See Made to Order</Link>
              <a href={buildWholesaleWhatsAppLink()} target="_blank" rel="noreferrer" data-testid="about-wholesale-link" className="text-[0.7rem] tracking-[0.3em] uppercase text-[color:var(--mb-primary)] hover:underline inline-flex items-center gap-1.5">
                <Truck size={14} /> Wholesale
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[color:var(--mb-border)] bg-[color:var(--mb-surface)] p-6">
              <p className="eyebrow mb-4">Visit Us</p>
              <p className="font-serif-display text-xl leading-tight">{BRAND_ADDRESS.full}</p>
              <p className="mt-4 text-sm text-[color:var(--mb-text-muted)]">Tuesday — Sunday · 10 AM to 8 PM · Monday closed</p>
            </div>
          </div>
        </div>
      </div>

      <Manifesto images={manifestoImages} />
    </div>
  );
}
